// components/Education.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaMedal, FaUserGraduate } from 'react-icons/fa';

const EducationCard = ({ icon, title, institution, year, description, gpa = null }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-primary">
      <div className="flex items-center mb-4">
        <div className="bg-primary bg-opacity-10 p-3 rounded-full text-primary mr-4">
          {icon}
        </div>
        <div>
          <h3 className="text-xl font-bold">{title}</h3>
          <p className="text-gray-600">{institution}</p>
        </div>
      </div>
      <div className="ml-16">
        <p className="text-gray-500 text-sm mb-2">{year}</p>
        <p className="text-gray-700">{description}</p>
        {gpa && <p className="text-primary font-semibold mt-2">Current GPA: {gpa}</p>}
      </div>
    </div>
  );
};

const CertificateCard = ({ title, issuer }) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center">
        <div className="bg-primary bg-opacity-10 p-2 rounded-full text-primary mr-3">
          <FaMedal size={16} />
        </div>
        <div>
          <h4 className="font-medium text-gray-800">{title}</h4>
          <p className="text-gray-600 text-sm">{issuer}</p>
        </div>
      </div>
    </div>
  );
};

const Education = () => {
  const certificates = [
    { title: "Python for Beginners", issuer: "University of Morotuwa, Sri Lanka" },
    { title: "Web Design for Beginners", issuer: "University of Morotuwa, Sri Lanka" },
    { title: "SQL (Intermediate)", issuer: "HackerRank" },
    { title: "React.js Essential Training", issuer: "LinkedIn" },
    { title: "JavaScript (Basics)", issuer: "HackerRank" },
    { title: "React Basics", issuer: "Coursera" },
  ];

  const achievements = [
    "Participated in Hult Prize (Qualifier Round)",
    "Participated in John Keells Competition (Shortlisted)",
  ];

  return (
    <section id="education" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-2">Education & Certifications</h2>
          <div className="w-24 h-1 bg-primary mx-auto"></div>
        </motion.div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <EducationCard 
              icon={<FaUserGraduate size={24} />}
              title="BEng. Software Engineering"
              institution="University of Westminster (IIT SL)"
              year="2023 - 2027"
              description="Studied Software Development, Computer System Fundamentals, Mathematics for Computing, 
              Trends in Computer Science, Web Design and Development, Object-Oriented Programming, Database Systems, 
              Software Engineering Principles and Practice."
              gpa="3.52"
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <EducationCard 
              icon={<FaGraduationCap size={24} />}
              title="GCE Advanced Level"
              institution="Sripalee College Horana"
              year="2022"
              description="Successfully completed A-Level education with focus on Science and Mathematics."
            />
            
            <div className="mt-8">
              <EducationCard 
                icon={<FaGraduationCap size={24} />}
                title="GCE Ordinary Level"
                institution="Sripalee College Horana"
                year="2018"
                description="Successfully completed O-Level education with distinction."
              />
            </div>
          </motion.div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-6 border-b-2 border-primary pb-2 inline-block">Certifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {certificates.map((cert, index) => (
                <CertificateCard 
                  key={index}
                  title={cert.title}
                  issuer={cert.issuer}
                />
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2}}
            viewport={{ once: true }}
          >
            <div>
              <h3 className="text-2xl font-bold mb-6 border-b-2 border-primary pb-2 inline-block">Achievements & Activities</h3>
              <div className="mb-8">
                <h4 className="text-lg font-semibold mb-3">Competitions & Achievements</h4>
                <ul className="space-y-2">
                  {achievements.map((achievement, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold mb-3">Volunteering & Activities</h4>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                  <h5 className="font-medium">Organizing Committee Member – Innovate X (2024)</h5>
                  <ul className="mt-2 space-y-1 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Contributed to program development, coordination, and outreach.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Assisted in securing support and mentoring South Asian undergraduate students.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      <span>Helped organize virtual sessions focused on entrepreneurship.</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mt-4">
                  <h5 className="font-medium">Member of Toastmasters Club, Horana - 2023</h5>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Education;